import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, QrCode, AlertCircle, CheckCircle, Clock, Search, Filter, Code, Backpack } from "lucide-react";
import { toast } from "sonner";



import AxiosDB from "@/Config/AxiosDB";
import moment from "moment";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FacturaPayloadView from "@/components/FacturaPayloadView";

export default function ComprobantesEmitidos({ user, onLogout }) {
  const [comprobantes, setComprobantes] = useState({
    rows: [],
    totalPages: 0,
    totalRow: 0

  });
  const [loading, setLoading] = useState(true);
  const [selectedComprobante, setSelectedComprobante] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);


  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [fechaDesde, setFechaDesde] = useState(moment().format());
  const [fechaHasta, setFechaHasta] = useState("");
  const [limite, setLimite] = useState("10");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadComprobantes();
  }, []);

  const searchTermfilter = searchTerm.trim();


  useEffect(() => {
    loadComprobantes();

    //eslint-disable-next-line
  }, [searchTermfilter, rowsPerPage, currentPage]);


  const loadComprobantes = async () => {
    try {
      const response = await AxiosDB.get(`/comprobantes/emitidos`, {
        params: {
          search: searchTermfilter,
          page: currentPage,
          cantidadRow: rowsPerPage,
        }
      });
      setComprobantes(response.data);
    } catch (error) {
      toast.error("Error al cargar comprobantes");
    } finally {
      setLoading(false);
    }
  };



  const clearFilters = () => {
    setSearchTerm("");
    setFechaDesde("");
    setFechaHasta("");
    setLimite("10");
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      valida: { variant: "default", className: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle },
      REJECTED: { variant: "destructive", className: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle },
      IN_PROCESS: { variant: "secondary", className: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
    };
    return estados[estado] || estados.IN_PROCESS;
  };

  const getEstadoComprobacionBadge = (estado) => {
    const estados = {
      FINISHED: { variant: "default", className: "bg-green-100 text-green-800 border-green-200" },
      rechazado: { variant: "destructive", className: "bg-red-100 text-red-800 border-red-200" },
      pendiente: { variant: "secondary", className: "bg-amber-100 text-amber-800 border-amber-200" },
    };
    return estados[estado] || estados.pendiente;
  };

  const getSeveridadBadge = (severidad) => {
    const severidades = {
      alta: "bg-red-100 text-red-800 border-red-200",
      media: "bg-amber-100 text-amber-800 border-amber-200",
      baja: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return severidades[severidad] || severidades.baja;
  };

  const openModal = (comprobante) => {
    setSelectedComprobante(comprobante);
    setModalOpen(true);
  };
  const openJsonModal = () => {
    setModalOpen(false);
    setJsonModalOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(amount);
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Comprobantes Emitidos">
      <Card data-testid="comprobantes-emitidos-card">
        <CardHeader>
          <CardTitle>Comprobantes Fiscales Emitidos</CardTitle>
          <CardDescription>
            Listado de todos los comprobantes fiscales que has emitido
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-700">Filtros de Búsqueda</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <Label htmlFor="search" className="text-sm">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    id="search"
                    placeholder="NCF, RNC, Nombre, Monto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>


              <div className="w-32">
                <Label htmlFor="limite" className="text-sm">Límite</Label>
                <Select value={limite} onValueChange={setLimite}>
                  <SelectTrigger data-testid="limite-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                  </SelectContent>
                </Select>
              </div>



            </div>
            <div className="flex items-end gap-4 mt-4">

              <div className="ml-auto text-sm text-slate-600">
                Se ha encontrado {comprobantes.totalRow}, Hay {comprobantes.totalPages} Paginas de {rowsPerPage} Registros.
              </div>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-8 text-slate-600">Cargando...</div>
          ) : comprobantes.rows.length === 0 ? (
            <div className="text-center py-8 text-slate-600">No hay comprobantes emitidos</div>
          ) : (
            <div className="overflow-x-auto" //style={{ fontSize: 5 }}
            >
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">NCF</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Monto</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">ITBIS</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Firma</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {comprobantes.rows.map((comp) => {
                    const estadoFirma = getEstadoBadge(comp.estado_firma);
                    const estadoComp = getEstadoComprobacionBadge(comp.estado_comprobacion);
                    const IconFirma = estadoFirma.icon;
                    return (
                      <tr key={comp.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`comprobante-row-${comp.id}`}>
                        <td className="py-3 px-4 text-sm">{comp.fecha_emision}</td>
                        <td className="py-3 px-4 text-sm font-mono">{comp.ncf}</td>
                        <td className="py-2 px-3">
                          <div className="font-medium text-slate-800 leading-tight truncate max-w-[300px]">{comp.nombre}</div>
                          <div className="text-xs text-slate-400">RNC: {comp.rnc}</div>
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-semibold">{formatCurrency(comp.monto)}</td>
                        <td className="py-3 px-4 text-sm text-right">{formatCurrency(comp.itbis)}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={estadoFirma.className}>
                            <IconFirma className="w-3 h-3 mr-1" />
                            {comp.estado_firma}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">

                          {comp.id_anulacion ? <Badge className="bg-red-100 text-red-800 border-red-200">
                            ANULADO
                          </Badge> :
                            <Badge className={estadoComp.className}>
                              {comp.estado_comprobacion}
                            </Badge>
                          }
                          {comp.errores.filter(e => e.valor !== '').length > 0 && (
                            <Badge variant="destructive" className="bg-red-100 text-red-800">
                              {comp.errores.filter(e => e.valor !== '').length}
                            </Badge>
                          )}
                        </td>

                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openModal(comp)}
                            data-testid={`view-details-${comp.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-6xl min-h-[95vh] w-[110vh] overflow-y-auto" data-testid="comprobante-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Detalle del Comprobante
            </DialogTitle>
            <DialogDescription>
              Información completa del comprobante fiscal
            </DialogDescription>
          </DialogHeader>
          {selectedComprobante && (
            <Tabs defaultValue="info" className="w-[105vh]">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="info" data-testid="payload-tab">Datos de Comprobante</TabsTrigger>
                <TabsTrigger value="factura" data-testid="factura-tab">Factura</TabsTrigger>
                <TabsTrigger value="payload" data-testid="payload-tab">Payload</TabsTrigger>
                <TabsTrigger value="response" data-testid="response-tab">Response</TabsTrigger>
                <TabsTrigger value="webhook" data-testid="webhook-tab">Webhook</TabsTrigger>
                {selectedComprobante.id_anulacion && <TabsTrigger value="anulaciones" data-testid="webhook-tab" style={{ color: 'red' }} >Anulaciones</TabsTrigger>}
              </TabsList>
              <TabsContent value="info" className="mt-4 h-[70vh]">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">NCF</p>
                      <p className="text-base font-semibold font-mono" data-testid="modal-ncf">{selectedComprobante.ncf}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Fecha de Emisión</p>
                      <p className="text-base font-semibold" data-testid="modal-fecha">{selectedComprobante.fecha_emision}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">RNC</p>
                      <p className="text-base font-semibold" data-testid="modal-rnc">{selectedComprobante.rnc}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Nombre</p>
                      <p className="text-base font-semibold" data-testid="modal-nombre">{selectedComprobante.nombre}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Monto</p>
                      <p className="text-base font-semibold" data-testid="modal-monto">{formatCurrency(selectedComprobante.monto)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">ITBIS</p>
                      <p className="text-base font-semibold" data-testid="modal-itbis">{formatCurrency(selectedComprobante.itbis)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">ID Documento</p>
                      <p className="text-base font-semibold" data-testid="modal-itbis">{selectedComprobante.invoice_id}</p>
                    </div>
                    {selectedComprobante.id_anulacion ? (<>
                      <div>
                        <p className="text-sm text-slate-600">ID Anulacion</p>
                        <p className="text-base font-semibold" data-testid="modal-itbis">{selectedComprobante.id_anulacion}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">Estado de Firma</p>
                        <Badge className={getEstadoBadge(selectedComprobante.estado_firma).className}>
                          {selectedComprobante.estado_firma}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Fecha y Hora de Anulacion</p>
                        <p className="text-base font-semibold" data-testid="modal-itbis">{selectedComprobante.response_anulacion[0]?.stampDate || 'Ver mas detalle en Anulaciones'}</p>
                      </div>


                    </>) : (
                      <>
                        <div className="space-y-2"></div>
                        <div className="space-y-2">
                          <p className="text-sm text-slate-600">Estado de Firma</p>
                          <Badge className={getEstadoBadge(selectedComprobante.estado_firma).className}>
                            {selectedComprobante.estado_firma}
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>



                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Estado de Comprobación</p>
                    <Badge className={getEstadoComprobacionBadge(selectedComprobante.estado_comprobacion).className}>
                      {selectedComprobante.estado_comprobacion}
                    </Badge>
                    {selectedComprobante.id_anulacion && <Badge className="bg-red-100 text-red-800 border-red-200">
                      ANULADO
                    </Badge>
                    }
                  </div>

                  {selectedComprobante.errores.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-700">Errores Detectados</p>
                      <div className="space-y-2">
                        {selectedComprobante.errores.map((error, index) => (
                          <div
                            key={index}
                            className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                            data-testid={`error-item-${index}`}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-mono text-sm font-semibold">{error.code} {error.codigo}</span>
                              <Badge className={getSeveridadBadge(error?.level || 'ERROR')}>
                                {error?.level || 'ERROR'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-700">{error.message} {error.valor}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Código QR</p>
                    <div className="flex items-center gap-2" >
                      <QrCode className="w-5 h-5 text-slate-600" />
                      <a
                        href={selectedComprobante.qr_data}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                        data-testid="qr-link"
                        style={{ width: 300 }}
                      >
                        {selectedComprobante.qr_data}
                      </a>
                    </div>
                  </div>

                </div>
              </TabsContent>
              <TabsContent value="factura" className="mt-4">
                <FacturaPayloadView payload={selectedComprobante.payload} formatCurrency={formatCurrency} />
              </TabsContent>
              <TabsContent value="payload" className="mt-4 h-[70vh]">
                <div className="bg-slate-900 rounded-lg p-4 overflow-auto h-[70vh] max-w-[100vh]">
                  <pre className="text-green-400 text-sm font-mono">
                    {JSON.stringify(selectedComprobante.payload || {}, null, 2)}
                  </pre>
                </div>
              </TabsContent>
              <TabsContent value="response" className="mt-4 h-[70vh]">
                <div className="bg-slate-900 rounded-lg p-4 overflow-auto h-[70vh] max-w-[100vh]">
                  <pre className="text-blue-400 text-sm font-mono">
                    {JSON.stringify(selectedComprobante.response || {}, null, 2)}
                  </pre>
                </div>
              </TabsContent>
              <TabsContent value="webhook" className="mt-4 h-[70vh]">
                <div className="bg-slate-900 rounded-lg p-4 overflow-auto h-[70vh] max-w-[100vh]">
                  <pre className="text-yellow-400 text-sm font-mono">
                    {JSON.stringify(selectedComprobante.webhook_resp || {}, null, 2)}
                  </pre>
                </div>
              </TabsContent>
              <TabsContent value="anulaciones" className="mt-4 h-[70vh]">
                <div className="bg-slate-900 rounded-lg p-4 overflow-auto h-[70vh] max-w-[100vh]">
                  <pre className="text-yellow-400 text-sm font-mono">
                    {JSON.stringify(selectedComprobante.response_anulacion || {}, null, 2)}
                  </pre>
                </div>
              </TabsContent>

            </Tabs>
          )}

        </DialogContent>
      </Dialog>


    </Layout>
  );
}